<template>
  <ContentWrapperControl>
    <div class="bg-white p-4 rounded-lg shadow mb-4 text-sm text-grey-darkest">
      <strong class="text-primary">{{ this.lang("lbl_0000330") }}</strong>
      <div class="text-xs text-grey-darker mt-2">
        View your registered participants and their details.
      </div>
    </div>

    <!-- Registrants List -->
    <div
      v-if="registrants.length === 0"
      class="bg-white p-6 rounded-lg shadow text-center text-grey-darker"
    >
      <p class="text-sm font-semibold">No registered participants found.</p>
      <p class="text-xs text-grey-dark mt-2">
        You can register participants through the registration page.
      </p>
    </div>
    <div
      v-else
      v-for="(registrant, index) in registrants"
      :key="index"
      class="bg-white rounded-lg shadow mb-4 overflow-hidden"
    >
      <!-- Summary View -->
      <div
        class="flex justify-between items-center px-4 py-3 cursor-pointer bg-white"
        @click="toggleExpand(index)"
      >
        <div>
          <div class="text-primary text-sm mb-2">
            <strong>{{ this.lang("lbl_0000322") + (index + 1) }}</strong>
          </div>
          <div class="text-dark-grey text-sm font-semibold uppercase">
            {{ registrant.lname }}, {{ registrant.fname }}
          </div>
          <div class="text-sm mt-2 text-grey-dark">
            {{
              this.lang("lbl_0000334") +
              ": " +
              getLabel(getRaceCategories, registrant.category)
            }}
            <template v-if="registrant.category !== '2'">
              ({{ getDistanceLabel(registrant.distance) }})
            </template>
          </div>
        </div>
        <img
          :src="
            isExpanded(index)
              ? require('@/assets/icons/papsi_dropup.svg')
              : require('@/assets/icons/papsi_dropdown.svg')
          "
          class="w-4 h-4"
          alt="Toggle"
        />
      </div>

      <!-- Expanded Form -->
      <transition name="fade">
        <div v-if="isExpanded(index)" class="p-4 border-t text-sm">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <!-- Name -->
            <div class="mb-4">
              <FormTextControl
                v-model="registrant.lname"
                name="lname"
                :placeholder="lang('lbl_0000145')"
                disabled
                uppercase
              />
            </div>
            <div class="mb-4">
              <FormTextControl
                v-model="registrant.fname"
                name="fname"
                :placeholder="lang('lbl_0000146')"
                disabled
                uppercase
              />
            </div>
            <div class="mb-4">
              <FormTextControl
                v-model="registrant.mname"
                name="mname"
                :placeholder="lang('lbl_0000325')"
                disabled
                uppercase
              />
            </div>

            <!-- Account No, Age, Contact -->
            <div class="mb-4">
              <FormTextControl
                v-model="registrant.account_no"
                name="account_no"
                :placeholder="lang('lbl_0000093')"
                disabled
                uppercase
              />
            </div>
            <div class="mb-4">
              <FormTextControl
                v-model="registrant.age"
                name="age"
                :placeholder="lang('lbl_0000103')"
                disabled
              />
            </div>
            <div class="mb-4">
              <FormTextControl
                v-model="registrant.email"
                name="email"
                :placeholder="lang('lbl_0000005')"
                disabled
              />
            </div>
            <div class="mb-4">
              <FormTextControl
                v-model="registrant.mobile"
                name="mobile"
                :placeholder="lang('lbl_0000007')"
                disabled
              />
            </div>

            <!-- Gender -->
            <div class="mb-4">
              <FormTextControl
                name="gender"
                :placeholder="'Gender'"
                :value="getLabel(getGenders, registrant.gender)"
                disabled
              />
            </div>

            <!-- Segment -->
            <div class="mb-4">
              <FormTextControl
                v-model="registrant.segment"
                name="segment"
                :placeholder="'Segment'"
                disabled
              />
            </div>

            <!-- Branch Service -->
            <div v-if="showBranchService(registrant.segment)" class="mb-4">
              <FormTextControl
                name="branch_service"
                :placeholder="'Branch Service'"
                :value="
                  getLabel(
                    getBranchServicesBySegment(registrant.segment),
                    registrant.branch_service
                  )
                "
                disabled
              />
            </div>

            <!-- Sponsor Type -->
            <div v-if="showSponsorType(registrant.segment)" class="mb-4">
              <FormTextControl
                name="sponsor_type"
                :placeholder="'Sponsor Type'"
                :value="getLabel(getSponsorTypes(), registrant.sponsor_type)"
                disabled
              />
            </div>

            <!-- Category -->
            <div class="mb-4">
              <FormTextControl
                name="category"
                :placeholder="'Category'"
                :value="getLabel(getRaceCategories, registrant.category)"
                disabled
              />
            </div>

            <!-- Distance -->
            <div v-if="registrant.category !== '2'" class="mb-4">
              <FormTextControl
                :value="getDistanceLabel(registrant.distance)"
                name="distance"
                :placeholder="'Distance'"
                disabled
              />
            </div>
          </div>

          <!-- QR Code Button -->
          <div class="mt-4 flex justify-end">
            <ButtonControl @click="openQR(registrant)">Show QR</ButtonControl>
          </div>
        </div>
      </transition>
    </div>

    <!-- QR Code Modal -->
    <ModalControl
      v-if="show_qr_modal"
      :show="show_qr_modal"
      title="Registrant QR Code"
      dismiss
      @closed="show_qr_modal = false"
    >
      <div class="flex justify-center p-4">
        <img
          :src="qr_image"
          alt="QR Code"
          class="w-40 h-40 border border-grey-light rounded"
        />
      </div>
    </ModalControl>
  </ContentWrapperControl>
</template>

<script>
import ContentWrapperControl from "@/components/controls/ContentWrapperControl.vue";
import FormTextControl from "@/components/controls/FormTextControl.vue";
import ButtonControl from "@/components/controls/ButtonControl.vue";
import ModalControl from "@/components/controls/ModalControl.vue";
import FunRun from "@/services/FunRun";
import mixin from "@/mixin.js";

export default {
  mixins: [mixin],
  components: {
    ContentWrapperControl,
    FormTextControl,
    ButtonControl,
    ModalControl,
  },
  data() {
    return {
      expanded_index: null,
      show_qr_modal: false,
      qr_image: "",
      registrants: [],
      segments: [],
    };
  },
  mounted() {
    this.$emit("showloader");
    this.$emit("shownavbar", {
      title: this.lang("lbl_0000329"),
      show: true,
      back: true,
    });
    this.$emit("nofooter", false);
    this.fetchSegments();
    FunRun.getRegistrants(this.$store.state.uuid)
      .then((response) => {
        const data = response?.data?.data;
        
        if (!Array.isArray(data) || data.length === 0) {
          this.registrants = [];
          return;
        }

        this.registrants = data.map((r) => ({
          lname: r.lname || "",
          fname: r.fname || "",
          mname: r.mname || "",
          account_no: r.acct_no || "",
          email: r.email || "",
          mobile: r.mobile || "",
          gender: r.gender || "",
          age: r.age || "",
          sponsor_type: r.sponsor_type || "",
          branch_service: r.branch_service || "",
          segment: r.segment?.fslot_name || "",
          distance: r.distance || "",
          category: r.category || "",
          qr_code: r.qr_code || "",
        }));
      })
      .catch((error) => {
        console.error(error);
        this.$emit("error", error);
        this.$router.push("/dashboard");
      })
      .finally(() => {
        this.$emit("hideloader");
      });
  },
  computed: {
    getSegments() {
      return this.segments;
    },
    getGenders() {
      return [
        { label: "Male", value: "M" },
        { label: "Female", value: "F" },
        { label: "Rather not say", value: "X" },
      ];
    },
    getRaceCategories() {
      return [
        { label: "Fun Run", value: "1" },
        { label: "Zumba", value: "2" },
        { label: "Both Fun Run & Zumba", value: "3" },
      ];
    },
  },
  methods: {
    toggleExpand(index) {
      this.expanded_index = this.expanded_index === index ? null : index;
    },
    isExpanded(index) {
      return this.expanded_index === index;
    },
    openQR(registrant) {
      if (registrant.qr_code) {
        // Use the registrant.qr_code string as data for the QR code
        this.qr_image = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(
          registrant.qr_code
        )}`;
        this.show_qr_modal = true;
      } else {
        this.$emit("error", "No QR code available for this registrant.");
      }
    },
    getRaceDistance() {
      return [
        { label: "3 KM", value: "03k" },
        { label: "5 KM", value: "05k" },
        { label: "10 KM", value: "10k" },
      ];
    },
    getDistanceLabel(value) {
      const found = this.getRaceDistance().find(
        (item) => item.value.toLowerCase() === value?.toLowerCase()
      );
      return found ? found.label : value;
    },

    fetchSegments() {
      FunRun.getSegment()
        .then((response) => {
          if (response?.data?.data) {
            const { segment, distance } = response.data.data;
            this.segments = segment.map((s) => ({
              label: s.label,
              value: s.value,
            }));
            const distance_map = {};
            distance.forEach((d) => {
              const key = Object.keys(d)[0];
              distance_map[key] = d[key];
            });
            this.$store.state.fun_run.segments = this.segments;
            this.$store.state.fun_run.distances = distance_map;
          }
        })
        .catch((error) => {
          this.$emit("error", error)
        });
    },
    getLabel(options, value) {
      const found = options.find((o) => o.value === value);
      return found ? found.label : "";
    },
    showBranchService(segment) {
      return ["MEM", "EMP"].includes(segment);
    },
    getBranchServicesBySegment(segment) {
      switch (segment) {
        case "MEM":
          return [
            { label: "PHILIPPINE ARMY", value: "PA" },
            { label: "PHILIPPINE NAVY", value: "PN" },
            { label: "PHILIPPINE AIR FORCE", value: "PAF" },
            { label: "RETIREE", value: "RET" },
            { label: "DEPENDENT", value: "DEP" },
            { label: "CIVILIAN EMPLOYEE", value: "CE" },
          ];
        case "EMP":
          return [
            { label: "MAIN OFFICE", value: "HO" },
            { label: "PSO", value: "PSO" },
          ];
        default:
          return [];
      }
    },
    showSponsorType(segment) {
      return segment === "SPR";
    },
    getSponsorTypes() {
      return [
        { label: "PLATINUM", value: "P" },
        { label: "GOLD", value: "G" },
        { label: "SILVER", value: "S" },
        { label: "BRONZE", value: "B" },
      ];
    },
  },
};
</script>
